// Pure canvas draw. Takes a 2D context + a snapshot of state and paints.
// No signal reads inside — callers pass what they need. This makes the drawing
// side reproducible and easy to reason about.

import type { Entity, Layer, Pan } from "../state/types.js";
import { worldToScreen } from "../utils/geometry.js";

// CSS custom property tokens the renderer reads from the shadow root's
// computed style. Kept in one place so a theme swap doesn't require touching
// the draw code.
export interface Palette {
  grid: string;
  gridMajor: string;
  primary: string;
  rulerBg: string;
  rulerTick: string;
  rulerLabel: string;
  rulerBorder: string;
}

export interface RenderInput {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  zoom: number;
  pan: Pan;
  // `gridSpacing` is authored in `gridUnit` and drives ruler LABELS.
  // `gridStepWorld` is the same value converted into world (dxfUnit) coords
  // via the mm-per-unit ratio -- this is what the grid/ruler render math uses
  // to place lines and ticks, so a "1 in" grid on a mm-authored drawing draws
  // one line every 25.4 world units and still reads "1 in" on the ruler.
  gridSpacing: number;
  gridStepWorld: number;
  gridUnit: string;
  showGrid: boolean;
  showGridRuler: boolean;
  entities: Entity[];
  layers: Record<string, Layer>;
  layerOrder: string[];
  selectedIds: Set<number>;
  palette: Palette;
}

// Ruler strip thickness in CSS pixels.
export const RULER_SIZE = 20;

export function render(input: RenderInput): void {
  const { ctx, width, height } = input;
  ctx.clearRect(0, 0, width, height);
  if (input.showGrid) drawGrid(input);
  drawEntities(input);
  if (input.showGridRuler) drawRuler(input);
}

// ---------- grid ----------

function drawGrid(input: RenderInput): void {
  const { ctx, width, height, zoom, pan, gridStepWorld, palette } = input;
  const step = gridStepWorld * zoom;
  if (step < 2) return;

  // Grid lines are anchored in *world* coordinates: vertical lines at
  //   world x = k * gridSpacing   =>   screen sx = k * step + pan.x
  // horizontal lines at
  //   world y = m * gridSpacing   =>   screen sy = -m * step + (pan.y + h/2)
  //
  // This uses the same worldToScreen transform as the entities, so the grid
  // moves in perfect lockstep with the drawing during pan / zoom.
  const yOrigin = pan.y + height / 2;
  const kMin = Math.ceil(-pan.x / step);
  const kMax = Math.floor((width - pan.x) / step);
  const mMin = Math.ceil((yOrigin - height) / step);
  const mMax = Math.floor(yOrigin / step);

  ctx.save();
  ctx.beginPath();
  ctx.strokeStyle = palette.grid || "rgba(128,128,128,0.15)";
  ctx.lineWidth = 0.5;
  for (let k = kMin; k <= kMax; k++) {
    const sx = k * step + pan.x;
    ctx.moveTo(sx, 0);
    ctx.lineTo(sx, height);
  }
  for (let m = mMin; m <= mMax; m++) {
    const sy = -m * step + yOrigin;
    ctx.moveTo(0, sy);
    ctx.lineTo(width, sy);
  }
  ctx.stroke();
  ctx.restore();

  // Origin axes — dashed, slightly stronger. These live at world x=0 / y=0,
  // which under the transform above coincide with the k=0 and m=0 grid lines.
  const originX = pan.x;
  const originY = yOrigin;
  ctx.save();
  ctx.setLineDash([4, 4]);
  ctx.strokeStyle = palette.gridMajor || "rgba(128,128,128,0.3)";
  ctx.lineWidth = 0.7;
  if (originY >= 0 && originY <= height) {
    ctx.beginPath();
    ctx.moveTo(0, originY);
    ctx.lineTo(width, originY);
    ctx.stroke();
  }
  if (originX >= 0 && originX <= width) {
    ctx.beginPath();
    ctx.moveTo(originX, 0);
    ctx.lineTo(originX, height);
    ctx.stroke();
  }
  ctx.restore();
}

// ---------- entities ----------

function drawEntities(input: RenderInput): void {
  const { ctx, height, zoom, pan, entities, layers, layerOrder, selectedIds, palette } = input;
  if (entities.length === 0) return;

  const byId = new Map<number, Entity>();
  for (const e of entities) byId.set(e.id, e);

  for (const layerName of layerOrder) {
    const layer = layers[layerName];
    if (!layer || !layer.visible) continue;
    const color = layer.color || "#888";
    // Layers whose name has a `bend` TOKEN (BEND, BENDS, BEND-UP, ...) are
    // treated as sheet-metal fold/construction layers -- open entities on
    // them render dashed. Closed shapes on such a layer are NOT dashed and
    // still participate in the fill pass, because a closed contour on a
    // "bend" layer is almost certainly a real cut region, not a fold line.
    const isBend = isBendLayerName(layerName);

    // ---- fill pass ----
    // Collect every closed ring on the layer into one combined Path2D and
    // fill it in a single call with the even-odd rule, so nested rings
    // cancel out of their shell (shell minus holes). This is the same
    // topology the GLB pipeline uses in rings_to_faces, and matches how
    // laser software renders the "core" area to be cut.
    const fillPath = new Path2D();
    let anyClosed = false;
    for (const id of layer.entityIds) {
      const entity = byId.get(id);
      if (!entity) continue;
      // Only genuinely-closed entities participate in the fill pass. Checking
      // by TYPE alone would sweep in open LWPOLYLINE/POLYLINE strokes (text
      // glyphs, construction lines, individual fillet arcs) and false-close
      // them into filled polygons -- an "A" stroked as three points becomes
      // a solid triangle.
      if (isOpenEntity(entity)) continue;
      const pts = entity.points;
      if (pts.length < 3) continue;
      const first = worldToScreen(pts[0]![0], pts[0]![1], zoom, pan, height);
      fillPath.moveTo(first.sx, first.sy);
      for (let i = 1; i < pts.length; i++) {
        const p = worldToScreen(pts[i]![0], pts[i]![1], zoom, pan, height);
        fillPath.lineTo(p.sx, p.sy);
      }
      fillPath.closePath();
      anyClosed = true;
    }
    if (anyClosed) {
      ctx.save();
      ctx.fillStyle = color + "25";
      ctx.fill(fillPath, "evenodd");
      ctx.restore();
    }

    // ---- stroke pass ----
    // Per-entity so each entity keeps its own stroke, selection highlight,
    // and hit-test parity with the rendered outlines. Dashed styling is
    // scoped to genuinely-open entities on bend layers so closed shapes
    // that happen to share a bend layer don't get styled as fold lines.
    for (const id of layer.entityIds) {
      const entity = byId.get(id);
      if (!entity) continue;
      const dashed = isBend && isOpenEntity(entity);
      strokeEntity(ctx, entity, color, selectedIds.has(id), zoom, pan, height, palette, dashed);
    }
  }
}

// True when this specific entity instance is topologically open (endpoints
// don't join). LINE and ARC are always open; CIRCLE is always closed;
// LWPOLYLINE and POLYLINE consult the DXF group-70 flags bit-1 which the
// parser stores on `data.flags`.
function isOpenEntity(entity: Entity): boolean {
  if (entity.type === "LINE" || entity.type === "ARC") return true;
  if (entity.type === "CIRCLE") return false;
  const flags = entity.data.flags ?? 0;
  return (flags & 1) === 0;
}

// True when the layer name has a standalone `bend` token: BEND, BENDS,
// BEND-UP, BEND-DOWN, SHEETMETAL_BEND_LINES, etc. `bend` must be preceded
// by start-of-string or a non-letter, and followed by end-of-string, a
// non-letter, or a trailing `s` (for the plural). This avoids false
// positives on substrings inside longer words like `Accubend`, `Rebend`,
// or `Bending`.
const BEND_LAYER_RE = /(^|[^a-z])bend(s(?=[^a-z]|$))?([^a-z]|$)/i;
function isBendLayerName(name: string): boolean {
  return BEND_LAYER_RE.test(name);
}

function strokeEntity(
  ctx: CanvasRenderingContext2D,
  entity: Entity,
  color: string,
  isSelected: boolean,
  zoom: number,
  pan: Pan,
  height: number,
  palette: Palette,
  dashed: boolean
): void {
  const pts = entity.points;
  if (pts.length < 1) return;

  const first = worldToScreen(pts[0]![0], pts[0]![1], zoom, pan, height);
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(first.sx, first.sy);
  for (let i = 1; i < pts.length; i++) {
    const p = worldToScreen(pts[i]![0], pts[i]![1], zoom, pan, height);
    ctx.lineTo(p.sx, p.sy);
  }
  // Only genuinely-closed polylines get the closing edge; open text glyphs
  // and construction strokes stay open so the phantom last-to-first segment
  // doesn't draw.
  if (!isOpenEntity(entity) && pts.length > 2) {
    ctx.closePath();
  }

  if (dashed) ctx.setLineDash([6, 4]);
  ctx.strokeStyle = isSelected ? palette.primary || "#fff" : color;
  ctx.lineWidth = isSelected ? 2.5 : 1.5;
  ctx.stroke();
  if (dashed) ctx.setLineDash([]);

  if (isSelected) {
    ctx.strokeStyle = "rgba(55,148,255,0.5)";
    ctx.lineWidth = 3;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.restore();
}

// ---------- ruler ----------
//
// Illustrator-style: 20px strip on TOP and LEFT only (right + bottom removed
// for a cleaner canvas). All ticks stay locked to world coordinates via the
// same transform the entities use, so pan/zoom keeps the ruler aligned.
//
// The 1-2-5-10-20-50 nice-number ladder drives the MAJOR-tick stride, not
// just the label stride: as the user zooms out, the effective distance
// between rendered majors stays at least MIN_MAJOR_PX so the strip never
// crowds or disappears, no matter how large the model is or how far out
// the user zooms. Ten minor subdivisions span each strided major interval
// (middle one drawn taller as a half-marker), and minors turn off once
// their own pixel spacing gets too tight to be useful.

function drawRuler(input: RenderInput): void {
  const { ctx, width, height, zoom, pan, gridSpacing, gridStepWorld, gridUnit, palette } = input;
  // Screen step derives from the world step (drawing-unit distance between
  // grid lines); label values derive from gridSpacing (in gridUnit) so the
  // ticks are laid out correctly and the labels still read in the user's
  // chosen grid unit.
  const stepPx = gridStepWorld * zoom;
  const size = RULER_SIZE;
  const yOrigin = pan.y + height / 2;

  // Adaptive major-tick stride (in units of gridSpacing) — always at least
  // MIN_MAJOR_PX apart on screen. This is what keeps the ruler readable at
  // extreme zoom-out instead of hiding it or crowding the majors.
  const MIN_MAJOR_PX = 48;
  const stride = niceStride(stepPx, MIN_MAJOR_PX);
  const majorPx = stride * stepPx;
  // Defensive floor: if stepPx is pathologically small (numerical extremes),
  // bail rather than spam thousands of ticks.
  if (majorPx < 20) return;

  // Minors are 10 subdivisions of each strided major interval. Only render
  // them once each minor is at least ~6 px wide.
  const showMinors = majorPx / 10 >= 6;

  // Strided iteration in units of `stride * gridSpacing`:
  //   world x at strided major = j * stride * gridSpacing
  //   screen sx = j * majorPx + pan.x
  const jMinX = Math.floor(-pan.x / majorPx) - 1;
  const jMaxX = Math.ceil((width - pan.x) / majorPx) + 1;
  const jMinY = Math.floor((yOrigin - height) / majorPx) - 1;
  const jMaxY = Math.ceil(yOrigin / majorPx) + 1;

  // ---- strips (top + left) ----
  ctx.save();
  ctx.fillStyle = palette.rulerBg;
  ctx.fillRect(0, 0, width, size); // top
  ctx.fillRect(0, 0, size, height); // left
  ctx.restore();

  ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.textBaseline = "top";

  // ---- top ruler (horizontal) ----
  // A major that falls in the corner region (sx < size) is skipped, but the
  // minors between it and the next visible major still render so the strip
  // never shows blank space near the edges.
  for (let j = jMinX; j <= jMaxX; j++) {
    const sx = j * majorPx + pan.x;
    if (sx >= size && sx <= width) {
      strokeVert(ctx, sx, 0, size, palette.rulerTick, 1);
      const text = formatTickLabel(j * stride * gridSpacing, gridUnit);
      ctx.fillStyle = palette.rulerLabel;
      ctx.textAlign = "left";
      ctx.fillText(text, sx + 3, 2);
    }

    if (!showMinors) continue;
    for (let m = 1; m < 10; m++) {
      const minorX = sx + (m * majorPx) / 10;
      if (minorX < size || minorX > width) continue;
      const isHalf = m === 5;
      const len = isHalf ? size * 0.6 : size * 0.3;
      strokeVert(ctx, minorX, size - len, size, palette.rulerTick, 1);
    }
  }

  // ---- left ruler (vertical) ----
  for (let j = jMinY; j <= jMaxY; j++) {
    const sy = -j * majorPx + yOrigin;
    if (sy >= size && sy <= height) {
      strokeHoriz(ctx, 0, sy, size, palette.rulerTick, 1);
      const text = formatTickLabel(j * stride * gridSpacing, gridUnit);
      ctx.fillStyle = palette.rulerLabel;
      // Rotate the label 90° CCW so it reads bottom-to-top like Illustrator's
      // vertical ruler. Anchor to the tick's y and offset by 3 px.
      ctx.save();
      ctx.translate(2, sy - 3);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = "left";
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }

    if (!showMinors) continue;
    for (let m = 1; m < 10; m++) {
      const minorY = sy - (m * majorPx) / 10;
      if (minorY < size || minorY > height) continue;
      const isHalf = m === 5;
      const len = isHalf ? size * 0.6 : size * 0.3;
      strokeHoriz(ctx, size - len, minorY, size, palette.rulerTick, 1);
    }
  }

  // Inner border seams so the ruler visually separates from the canvas.
  ctx.save();
  ctx.strokeStyle = palette.rulerBorder;
  ctx.lineWidth = 1;
  ctx.beginPath();
  // Horizontal seam under the top strip.
  ctx.moveTo(0, size + 0.5);
  ctx.lineTo(width, size + 0.5);
  // Vertical seam right of the left strip.
  ctx.moveTo(size + 0.5, 0);
  ctx.lineTo(size + 0.5, height);
  ctx.stroke();
  ctx.restore();

  // Corner: show the current ruler unit as a tiny label in the top-left
  // intersection so users can see at a glance what "1" on the ruler means.
  ctx.save();
  ctx.fillStyle = palette.rulerLabel;
  ctx.font = "bold 10px ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(gridUnit, size / 2, size / 2);
  ctx.restore();
}

// Pick a stride from the 1-2-5-10-20-50 nice-number ladder such that the
// on-screen gap between successive strided ticks is at least `minPx` pixels
// wide. Stride is expressed in units of gridSpacing (integer ≥ 1). Returns 1
// when zoomed in enough that every gridSpacing tick already clears `minPx`.
function niceStride(stepPx: number, minPx: number): number {
  if (stepPx >= minPx) return 1;
  const raw = minPx / stepPx;
  const power = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const mult of [1, 2, 5]) {
    if (mult * power >= raw) return Math.round(mult * power);
  }
  return Math.round(10 * power);
}

function strokeVert(
  ctx: CanvasRenderingContext2D,
  x: number,
  y1: number,
  y2: number,
  color: string,
  lineWidth: number
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  // Half-pixel alignment for crisp 1px strokes.
  const sx = Math.round(x) + 0.5;
  ctx.moveTo(sx, y1);
  ctx.lineTo(sx, y2);
  ctx.stroke();
  ctx.restore();
}

function strokeHoriz(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y: number,
  x2: number,
  color: string,
  lineWidth: number
): void {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  const sy = Math.round(y) + 0.5;
  ctx.moveTo(x1, sy);
  ctx.lineTo(x2, sy);
  ctx.stroke();
  ctx.restore();
}

// Format a numeric tick label so integers stay integer, fractions show a small
// number of decimals, and the unit is only appended at the origin so the
// ruler doesn't look busy.
function formatTickLabel(value: number, unit: string): string {
  // Guard tiny floats coming out of k*gridSpacing.
  const rounded = Math.abs(value) < 1e-9 ? 0 : value;
  let text: string;
  if (Number.isInteger(rounded)) text = String(rounded);
  else text = Number(rounded.toFixed(3)).toString();
  if (rounded === 0) text = `0 ${unit}`;
  return text;
}
